import type { CommandHandlerFn } from '../command-handler'

export type ScreenCaptureProvider = (() => Promise<Buffer>) | 'web' | 'desktop'

export interface ScreenHandlerOptions {
  screenCapture?: ScreenCaptureProvider
  screenCaptureUrl?: string
}

export function createScreenHandlers(options: ScreenHandlerOptions): Record<string, CommandHandlerFn> {
  if (!options.screenCapture) return {}

  const capture = buildCaptureFn(options)

  return {
    'screen:capture': async (params?: { quality?: number; format?: string }) => {
      const data = await capture()
      return {
        data,
        format: params?.format || 'png',
        width: 0,
        height: 0,
        timestamp: new Date().toISOString(),
      }
    },

    'screen:thumbnail': async (params?: { width?: number }) => {
      const data = await capture()
      return {
        data,
        format: 'png',
        width: params?.width || 320,
        height: 0,
        timestamp: new Date().toISOString(),
      }
    },
  }
}

function buildCaptureFn(options: ScreenHandlerOptions): () => Promise<Buffer> {
  if (typeof options.screenCapture === 'function') {
    return options.screenCapture
  }

  if (options.screenCapture === 'web') {
    return async () => {
      try {
        // puppeteer 为可选依赖，运行时动态加载
        const puppeteer = await (Function('return import("puppeteer")')() as Promise<any>)
        const browser = await puppeteer.launch({ headless: true })
        const page = await browser.newPage()
        await page.goto(options.screenCaptureUrl || 'http://localhost:3000')
        const screenshot = await page.screenshot({ type: 'png' })
        await browser.close()
        return Buffer.from(screenshot)
      } catch (err: any) {
        throw new Error(`Web 截屏失败: ${err.message}`)
      }
    }
  }

  if (options.screenCapture === 'desktop') {
    return async () => {
      try {
        // screenshot-desktop 为可选依赖，运行时动态加载
        const screenshotDesktop = await (Function('return import("screenshot-desktop")')() as Promise<any>)
        return await screenshotDesktop.default()
      } catch (err: any) {
        throw new Error(`桌面截屏失败: ${err.message}`)
      }
    }
  }

  return async () => Buffer.alloc(0)
}
